import { map } from 'lodash';
import React, { createContext, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Switch, useLocation, useRouteMatch } from 'react-router';
import { gql, useQuery } from 'urql';
import { Loading, PrivateRoute, TerminalHandlings, TerminalHome } from './';
import { HacpackQueryResponse, Terminal } from './../types';
import QueryLoader from './QueryLoader';
import StorageReport from './StorageReport';
import { terminalHandlingsPages } from './TerminalHandlings';
import TerminalSegregation from './TerminalSegregation';
import TerminalAreasRisk from './TerminalAreasRisk';
import TerminalUnitSearch from './TerminalUnitSearch';
import PmisImporter from './PmisImporter';
import TerminalIspsCode from './TerminalIspsCode';
import HandlingAddPage from './Handling/HandlingAddPage';
import { DeepPartial } from 'tsdef/dist/node';
import TerminalHazardClassSummaryPage from './TerminalHazardClassSummaryPage';
import StorageReportNew from './StorageReportNew';
import TerminalHandlingsApproval from './TerminalHandlingsApproval';
import TerminalSendData from './TerminalSendData';

const terminaRootQuery = gql`
query terminal($id: Int!) {
    terminal (id: $id) {
        id
        name
        slug
    }
}
`;

type TerminalRootProps = {
    id: number
}

export type TerminalContextObject = {
    terminal: DeepPartial<Terminal>|null
};

export const TerminalContext = createContext<TerminalContextObject>({
    terminal: null
});

const TerminalRoot = ({ id }: TerminalRootProps) => {
    const [result] = useQuery<{ terminal: Terminal }, { id: number }>({
        query: terminaRootQuery,
        variables: { id }
    });

    const { data/*, fetching, error*/ } = result;

    const terminal = useMemo(() => data?.terminal, [data]);

    const { t } = useTranslation('common');
    const { url, params } = useRouteMatch();

    const { state, ...location } = useLocation<{ parentUrl: string }>();

    if (terminal?.id !== id || terminal.slug !== (params as any)['slug']) {
        return <Loading />;
    }

    return (
        <TerminalContext.Provider value={{ terminal }}>
            <Helmet>
                <title>{terminal.name} :: {t('app.title')}</title>
            </Helmet>

            <Switch location={{ state: { ...(state ?? {}), parentUrl: url, terminal }, ...location }}>
                <PrivateRoute exact path={`${url}`}>
                    <TerminalHome key={terminal.id} id={terminal.id} />
                </PrivateRoute>

                {map(terminalHandlingsPages, (page) => {
                    const slug = t(`terminal.${page.key}.slug`);
                    const baseUrl = `${url}/:page(${slug})`

                    return <PrivateRoute key={page.key} path={baseUrl}>
                        <QueryLoader<HacpackQueryResponse, { id: number }> query={page.query} variables={{ id: (terminal.id as number) }} reloadInterval={60000} render={(data, reload) => {
                            return <TerminalHandlings page={page} terminal={data.terminal as Terminal} />
                        }} />
                    </PrivateRoute>
                })}

                <PrivateRoute path={`${url}/${t('approvazione')}`}>
                    <TerminalHandlingsApproval id={terminal.id} />
                </PrivateRoute>

                <PrivateRoute path={[`${url}/${t('invio-dati')}`]}>
                    <TerminalSendData id={terminal.id} />
                </PrivateRoute>

                <PrivateRoute path={[`${url}/${t('rischio')}`]}>
                    <TerminalAreasRisk id={terminal.id} />
                </PrivateRoute>

                <PrivateRoute exact path={`${url}/${t('segregazione')}`}>
                    <TerminalSegregation id={terminal.id} />
                </PrivateRoute>

                <PrivateRoute path={`${url}/${t('cerca-container')}`}>
                    <TerminalUnitSearch id={terminal.id} />
                </PrivateRoute>

                <PrivateRoute exact path={`${url}/${t('terminal.storageReport.slug')}`}>
                    {
                        terminal.name === 'SINTERMAR'
                            ? <StorageReportNew terminalName={terminal.name} />
                            : <StorageReport id={terminal.id} />
                    }
                </PrivateRoute>

                <PrivateRoute exact path={`${url}/${t('pmisImporter.slug')}`}>
                    {process.env.NODE_ENV === 'development' ? <PmisImporter id={terminal.id} /> : <PmisImporter id={terminal.id} />}
                </PrivateRoute>

                <PrivateRoute exact path={`${url}/${t('isps-code')}`}>
                    <TerminalIspsCode id={terminal.id} />
                </PrivateRoute>

                <PrivateRoute path={`${url}/${t('nuovo-previsionale')}`}>
                    <HandlingAddPage />
                </PrivateRoute>

                <PrivateRoute path={`${url}/${t('conteggio-teu')}`}>
                    <TerminalHazardClassSummaryPage id={terminal.id} />
                </PrivateRoute>

            </Switch>
        </TerminalContext.Provider>
    );
}

export default TerminalRoot;